import { useEffect, useState } from 'react'
import {
  adminListProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  reorderProducts,
} from '../../lib/products.js'
import { money } from '../../lib/format.js'
import ProductForm from './ProductForm.jsx'
import './admin.css'

export default function AdminDashboard() {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState(null) // product object, {} for new, or null
  const [busyId, setBusyId] = useState(null)
  const [reordering, setReordering] = useState(false)

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      setProducts(await adminListProducts())
    } catch (err) {
      setError(err.message || 'Could not load products.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const onSave = async (values, id) => {
    if (id) {
      const updated = await updateProduct(id, values)
      setProducts((list) => list.map((p) => (p.id === id ? updated : p)))
    } else {
      const created = await createProduct(values)
      setProducts((list) => [created, ...list])
    }
    setEditing(null)
  }

  const onDelete = async (product) => {
    if (!window.confirm(`Delete “${product.name}”? This cannot be undone.`)) return
    setBusyId(product.id)
    try {
      await deleteProduct(product.id)
      setProducts((list) => list.filter((p) => p.id !== product.id))
    } catch (err) {
      alert(err.message || 'Delete failed.')
    } finally {
      setBusyId(null)
    }
  }

  // Move a product up/down in the display order and persist the new order.
  const move = async (index, dir) => {
    const j = index + dir
    if (j < 0 || j >= products.length) return
    const next = [...products]
    ;[next[index], next[j]] = [next[j], next[index]]
    setProducts(next) // optimistic
    setReordering(true)
    setError('')
    try {
      await reorderProducts(next)
    } catch (err) {
      setError(err.message || 'Could not save the new order.')
      load() // revert to server truth
    } finally {
      setReordering(false)
    }
  }

  const toggle = async (product, field) => {
    setBusyId(product.id)
    try {
      const updated = await updateProduct(product.id, { [field]: !product[field] })
      setProducts((list) => list.map((p) => (p.id === product.id ? updated : p)))
    } catch (err) {
      alert(err.message || 'Update failed.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <>
      <div className="admin__head">
          <div>
            <h1>Products</h1>
            <p>{products.length} item{products.length === 1 ? '' : 's'} in your products.</p>
          </div>
          <div className="admin__head-actions">
            <button className="btn btn--outline" onClick={load} disabled={loading}>
              Refresh
            </button>
            <button className="btn btn--primary" onClick={() => setEditing({})}>
              + Add product
            </button>
          </div>
        </div>

        {error && <div className="admin-alert admin-alert--error">{error}</div>}

        {loading ? (
          <p className="admin__empty">Loading products…</p>
        ) : products.length === 0 ? (
          <p className="admin__empty">No products yet. Add your first one.</p>
        ) : (
          <div className="admin__table-wrap">
            <table className="admin__table">
              <thead>
                <tr>
                  <th aria-label="Reorder">Order</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Purity</th>
                  <th>In stock</th>
                  <th>Featured</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {products.map((p, i) => (
                  <tr key={p.id} className={busyId === p.id ? 'is-busy' : ''}>
                    <td>
                      <div className="admin__reorder">
                        <button
                          className="admin__move"
                          onClick={() => move(i, -1)}
                          disabled={reordering || i === 0}
                          aria-label={`Move ${p.name} up`}
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          className="admin__move"
                          onClick={() => move(i, 1)}
                          disabled={reordering || i === products.length - 1}
                          aria-label={`Move ${p.name} down`}
                          title="Move down"
                        >
                          ↓
                        </button>
                      </div>
                    </td>
                    <td>
                      <strong>{p.name}</strong>
                      <span className="admin__slug">{p.slug}</span>
                    </td>
                    <td>{p.category}</td>
                    <td>
                      {money(p.price)}
                      {p.compare_at_price && (
                        <span className="admin__was">{money(p.compare_at_price)}</span>
                      )}
                    </td>
                    <td>{p.purity || '—'}</td>
                    <td>
                      <button
                        className={`admin__toggle ${p.in_stock ? 'is-on' : ''}`}
                        onClick={() => toggle(p, 'in_stock')}
                        disabled={busyId === p.id}
                        aria-pressed={p.in_stock}
                      >
                        {p.in_stock ? 'In stock' : 'Out'}
                      </button>
                    </td>
                    <td>
                      <button
                        className={`admin__toggle ${p.featured ? 'is-on' : ''}`}
                        onClick={() => toggle(p, 'featured')}
                        disabled={busyId === p.id}
                        aria-pressed={p.featured}
                      >
                        {p.featured ? 'Featured' : 'No'}
                      </button>
                    </td>
                    <td>
                      <div className="admin__row-actions">
                        <button className="admin__link" onClick={() => setEditing(p)}>
                          Edit
                        </button>
                        <button
                          className="admin__link admin__link--danger"
                          onClick={() => onDelete(p)}
                          disabled={busyId === p.id}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {editing !== null && (
        <ProductForm
          product={editing}
          existingIds={products.map((p) => p.id)}
          onSave={onSave}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  )
}
